import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  LucideAngularModule,
  ArrowLeft,
  Trash2,
  Pencil,
  Plus,
  FileText,
  CheckCircle,
  Check,
  MessageSquare,
  Image,
  Video,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  X,
  Info,
} from 'lucide-angular';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { AssignmentService } from '../../services/assignment.service';
import {
  ASSIGNMENT_THEME_COLOR,
  AssignmentQuestion,
  QuestionType,
} from '../../services/assignment.models';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';
import { ConfirmDialogComponent } from '../../../../../common/components/confirm-dialog/confirm-dialog';
import { EditQuestionSheetComponent } from './edit-question-sheet/edit-question-sheet';
import { EditQuestionSheetData } from './edit-question-sheet/question-edit.service';

@Component({
  selector: 'app-step-questions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    ConfirmDialogComponent,
    OverlayModule,
  ],
  templateUrl: './step-questions.html',
  styleUrl: './step-questions.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepQuestionsComponent {
  private readonly assignmentService = inject(AssignmentService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly bottomSheet = inject(MatBottomSheet);

  @Output() readonly next = new EventEmitter<void>();
  @Output() readonly back = new EventEmitter<void>();

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;
  protected readonly title$ = this.assignmentService.title$;
  protected readonly questions$ = this.assignmentService.questions$;
  protected readonly error$ = this.assignmentService.error$;
  protected readonly QuestionType = QuestionType;

  protected readonly icons = {
    ArrowLeft,
    Trash2,
    Pencil,
    Plus,
    FileText,
    CheckCircle,
    Check,
    MessageSquare,
    Image,
    Video,
    ChevronDown,
    ChevronUp,
    MoreVertical,
    X,
    Info,
  };

  private readonly OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  // ============================================
  // TITLE EDITING
  // ============================================

  protected isEditingTitle = false;
  protected editingTitleValue = '';

  @ViewChild('titleInput') private titleInput?: ElementRef<HTMLTextAreaElement>;

  protected startTitleEdit(currentTitle: string): void {
    this.editingTitleValue = currentTitle;
    this.isEditingTitle = true;
    this.cdr.markForCheck();
    // Auto-resize after the textarea renders
    setTimeout(() => this.autoResizeTitleInput());
  }

  protected onTitleInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.editingTitleValue = textarea.value;
    this.autoResizeTitleInput();
  }

  private autoResizeTitleInput(): void {
    const el = this.titleInput?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  protected confirmTitleEdit(): void {
    const trimmed = this.editingTitleValue.trim();
    if (trimmed) {
      this.assignmentService
        .saveTitle(trimmed)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
    this.isEditingTitle = false;
    this.cdr.markForCheck();
  }

  protected cancelTitleEdit(): void {
    this.isEditingTitle = false;
    this.cdr.markForCheck();
  }

  // ============================================
  // TITLE INFO POPOVER
  // ============================================

  protected showTitleInfo = false;

  protected readonly titleInfoPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 6 },
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -6 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
  ];

  protected toggleTitleInfo(): void {
    this.showTitleInfo = !this.showTitleInfo;
    this.cdr.markForCheck();
  }

  protected closeTitleInfo(): void {
    this.showTitleInfo = false;
    this.cdr.markForCheck();
  }

  // CDK overlay positions: prefer bottom-end, fallback top-end
  protected readonly menuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  // Question card menu state (index of the question whose menu is open, or null)
  protected questionMenuOpenIdx: number | null = null;

  // Add question menu state
  private readonly showAddMenu$$ = new BehaviorSubject<boolean>(false);
  protected readonly showAddMenu$ = this.showAddMenu$$.asObservable();

  // Saving state
  private readonly isSaving$$ = new BehaviorSubject<boolean>(false);
  protected readonly isSaving$ = this.isSaving$$.asObservable();

  // Check if can publish
  protected readonly canPublish$: Observable<boolean> = this.questions$.pipe(
    map((questions) => questions.length > 0),
  );

  // ============================================
  // QUESTION TYPE LABELS
  // ============================================

  protected getQuestionTypeLabel(type: QuestionType): string {
    switch (type) {
      case QuestionType.MULTIPLE_CHOICE:
        return 'Multiple choice';
      case QuestionType.SHORT_ANSWER:
        return 'Short answer';
      case QuestionType.ESSAY:
        return 'Essay';
      default:
        return 'Question';
    }
  }

  protected getQuestionTypeIcon(type: QuestionType): any {
    switch (type) {
      case QuestionType.MULTIPLE_CHOICE:
        return this.icons.CheckCircle;
      case QuestionType.SHORT_ANSWER:
        return this.icons.MessageSquare;
      case QuestionType.ESSAY:
        return this.icons.FileText;
      default:
        return this.icons.FileText;
    }
  }

  // ============================================
  // HELPERS
  // ============================================

  protected getOptionLetter(index: number): string {
    return this.OPTION_LETTERS[index] ?? String(index + 1);
  }

  // ============================================
  // QUESTION CARD MENU
  // ============================================

  protected toggleQuestionMenu(index: number): void {
    this.questionMenuOpenIdx = this.questionMenuOpenIdx === index ? null : index;
    this.cdr.markForCheck();
  }

  protected closeQuestionMenu(): void {
    if (this.questionMenuOpenIdx !== null) {
      this.questionMenuOpenIdx = null;
      this.cdr.markForCheck();
    }
  }

  // ============================================
  // REORDER QUESTIONS
  // ============================================

  protected moveQuestion(index: number, direction: 'up' | 'down'): void {
    const questions = [...this.assignmentService.questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= questions.length) {
      return;
    }

    // Swap
    [questions[index], questions[targetIndex]] = [questions[targetIndex], questions[index]];

    // Re-order
    const reorderedQuestions = questions.map((q, i) => ({
      ...q,
      order: i + 1,
    }));
    this.assignmentService.updateQuestions(reorderedQuestions);
    this.questionMenuOpenIdx = null;
  }

  // ============================================
  // EDIT QUESTION (MatBottomSheet)
  // ============================================

  protected openEditQuestion(questionId: string): void {
    this.questionMenuOpenIdx = null;

    const question = this.assignmentService.questions.find((q) => q.id === questionId);
    if (!question) {
      return;
    }

    this.bottomSheet.open(EditQuestionSheetComponent, {
      data: { question } as EditQuestionSheetData,
    });
  }

  // ============================================
  // DELETE QUESTION (with confirmation)
  // ============================================

  @ViewChild('confirmDialog')
  private confirmDialog!: ConfirmDialogComponent;

  @ViewChild('backConfirmDialog')
  private backConfirmDialog!: ConfirmDialogComponent;

  private pendingDeleteQuestionId: string | null = null;

  protected requestDeleteQuestion(questionId: string): void {
    this.questionMenuOpenIdx = null;
    this.pendingDeleteQuestionId = questionId;
    this.confirmDialog.isOpen = true;
  }

  protected confirmDeleteQuestion(): void {
    if (!this.pendingDeleteQuestionId) {
      return;
    }

    const questions = this.assignmentService.questions.filter(
      (q) => q.id !== this.pendingDeleteQuestionId,
    );
    // Re-order questions
    const reorderedQuestions = questions.map((q, index) => ({
      ...q,
      order: index + 1,
    }));
    this.assignmentService.updateQuestions(reorderedQuestions);
    this.pendingDeleteQuestionId = null;
  }

  protected cancelDeleteQuestion(): void {
    this.pendingDeleteQuestionId = null;
  }

  // ============================================
  // ADD QUESTION
  // ============================================

  protected toggleAddMenu(): void {
    this.showAddMenu$$.next(!this.showAddMenu$$.value);
  }

  protected closeAddMenu(): void {
    setTimeout(() => {
      this.showAddMenu$$.next(false);
    }, 150);
  }

  protected addQuestion(type: QuestionType): void {
    const questions = this.assignmentService.questions;
    const newOrder = questions.length + 1;
    const newId = `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    const defaultTextByType: Record<QuestionType, string> = {
      [QuestionType.ESSAY]: 'New essay question',
      [QuestionType.MULTIPLE_CHOICE]: 'New multiple choice question',
      [QuestionType.SHORT_ANSWER]: 'New short answer question',
    };

    let newQuestion: AssignmentQuestion = {
      id: newId,
      type,
      questionText: defaultTextByType[type] ?? 'New question',
      options: [],
      points: 1,
      order: newOrder,
      scoringGuideItemId: null,
    };

    if (type === QuestionType.MULTIPLE_CHOICE) {
      newQuestion.options = [
        { id: `opt_${Date.now()}_1`, text: 'Option A', isCorrect: true },
        { id: `opt_${Date.now()}_2`, text: 'Option B', isCorrect: false },
        { id: `opt_${Date.now()}_3`, text: 'Option C', isCorrect: false },
        { id: `opt_${Date.now()}_4`, text: 'Option D', isCorrect: false },
      ];
    }

    this.assignmentService.updateQuestions([...questions, newQuestion]);
    this.showAddMenu$$.next(false);
  }

  // ============================================
  // NAVIGATION
  // ============================================

  protected onBack(): void {
    this.backConfirmDialog.isOpen = true;
  }

  protected confirmBack(): void {
    this.back.emit();
  }

  protected onPublish(): void {
    this.isSaving$$.next(true);

    // Save questions first, then navigate to settings
    this.assignmentService
      .saveQuestions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving$$.next(false);

          this.amplitudeService.trackEvent(
            'assignment_wizard_step_questions_completed',
            {
              assignment_id: this.assignmentService.assignmentId,
              total_questions: this.assignmentService.questions.length,
            },
          );

          // Navigate to settings step
          // Note: Don't emit next here as setStep already handles navigation
          this.assignmentService.setStep(4);
        },
        error: (err: any) => {
          this.isSaving$$.next(false);
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'questions_save_failed',
            error: err?.message || 'Unknown error',
            step: 3,
          });
        },
      });
  }
}
